import React from "react";

import {
  faCopy,
  faCut,
  faObjectUngroup,
  faPen,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { observer } from "mobx-react-lite";

import { toastActions } from "src/design-system/Toast/utils";
import { Toolbar } from "src/design-system/Toolbar";
import { NodeControlsToolbar } from "src/flowGraph/nodeControls/NodeControlsToolbar";
import { useIsNodeMeasured } from "src/flowGraph/nodeControls/useIsNodeMeasured";
import { useCanAuthoringEditFlowVersion } from "src/hooks/useCanAuthoringEditFlowVersion";
import { getCreatableNodes } from "src/nodeAdding/PasteNodesCard";
import { AuthorPageParams } from "src/router/urls";
import { useGraphStore } from "src/store/StoreProvider";
import { pluralize } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

type Props = {
  onMouseEnter: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave: React.MouseEventHandler<HTMLDivElement>;
  id: string;
  close: () => void;
};

export const GroupControls: React.FC<Props> = observer(
  ({ onMouseEnter, onMouseLeave, close, id }) => {
    const {
      setIsUngroupModalVisible,
      setIsRenameNodeModalVisible,
      setGroupToChangeId,
      setIsDeleteGroupModalVisible,
      copyToClipboard,
      groups,
      setSelectedNode,
      deleteNode,
    } = useGraphStore();
    const { wsId } = useParamsDecode<AuthorPageParams>();

    const canModifyGraph = useCanAuthoringEditFlowVersion();
    const isMeasured = useIsNodeMeasured(id);

    const group = groups.get(id);

    const copyGroupToClipboard = async (isCutting?: boolean) => {
      if (group) {
        const copiedClipboard = await copyToClipboard([group], wsId);
        if (copiedClipboard) {
          if (isCutting) {
            await deleteNode(group, true);
            setSelectedNode(undefined);
          }
          toastActions.success({
            title: `${pluralize(
              getCreatableNodes(copiedClipboard.nodes).length,
              "node",
            )} ${isCutting ? "cut" : "copied"}`,
          });
        } else {
          toastActions.failure({
            title: "Copy to clipboard failed",
            description: "See the documentation for more information",
          });
        }
      }
    };

    if (!isMeasured) {
      return null;
    }

    return (
      <NodeControlsToolbar
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <Toolbar dataLoc="group-controls" onClick={(e) => e.stopPropagation()}>
          {canModifyGraph && (
            <>
              <Toolbar.IconButton
                icon={faPen}
                tooltip="Rename"
                onClick={() => {
                  setGroupToChangeId(id);
                  close();
                  setIsRenameNodeModalVisible(true);
                }}
              />
              <Toolbar.IconButton
                dataLoc="cut-group"
                icon={faCut}
                tooltip="Cut"
                onClick={() => copyGroupToClipboard(true)}
              />
            </>
          )}
          <Toolbar.IconButton
            dataLoc="copy-group"
            icon={faCopy}
            tooltip="Copy"
            onClick={() => copyGroupToClipboard(false)}
          />
          {canModifyGraph && (
            <>
              <Toolbar.IconButton
                dataLoc="ungroup-node-group"
                icon={faObjectUngroup}
                tooltip="Ungroup"
                onClick={() => {
                  setGroupToChangeId(id);
                  close();
                  setIsUngroupModalVisible(true);
                }}
              />
              <Toolbar.IconButton
                dataLoc="delete-node-group"
                icon={faTrashCan}
                tooltip="Delete"
                onClick={() => {
                  setGroupToChangeId(id);
                  close();
                  setIsDeleteGroupModalVisible(true);
                }}
              />
            </>
          )}
        </Toolbar>
      </NodeControlsToolbar>
    );
  },
);
